import React, { FC, useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { toast } from 'react-toastify';
import eFormType from '../../main/assets/enums/eFormType';
import { ApiConstants } from '../../main/constants/ApiConstants';
import axiosInstance from '../../main/initializers/axios';
import IDailyInventory from '../../main/interfaces/IDailyInventory';
import IProduct from '../../main/interfaces/IProduct';
import './DailyInventoryGrid.css';
import i18n from 'i18next';

interface Props {
  tempItem?: IDailyInventory | null;
  products: IProduct[];
  modalType: eFormType;
  createOrUpdate: boolean;
  onSubmitData: () => void;
}

const toDateInput = (value?: string) => {
  if (!value) return new Date().toISOString().slice(0, 10);
  return value.slice(0, 10);
};

const DailyInventoryForm: FC<Props> = ({ tempItem, products, modalType, createOrUpdate, onSubmitData }) => {
  const [productId, setProductId] = useState<number>(modalType === eFormType.Edit ? Number(tempItem?.productId ?? 0) : 0);
  const [date, setDate] = useState<string>(toDateInput(tempItem?.date));
  const [quantity, setQuantity] = useState<string>(String(modalType === eFormType.Edit ? tempItem?.quantity ?? 0 : 0));
  const [warningQuantity, setWarningQuantity] = useState<string>(String(modalType === eFormType.Edit ? tempItem?.warningQuantity ?? 0 : 0));

  const selectedProduct = useMemo(() => products.find((item) => Number(item.id) === Number(productId)) ?? null, [productId, products]);

  const submit = async () => {
    if (!productId) {
      toast.error(i18n.t('dailyInventory.messages.selectProduct', { defaultValue: 'Select product' }));
      return;
    }

    const payload = {
      id: Number(tempItem?.id ?? 0),
      productId,
      date,
      quantity: Number(quantity),
      warningQuantity: Number(warningQuantity),
    };

    const response = await axiosInstance.post(ApiConstants.dailyInventory.update, payload);
    if (response.status === 200) {
      toast.success(i18n.t('dailyInventory.messages.saved', { defaultValue: 'Saved successfully' }));
      onSubmitData();
      return;
    }

    toast.error(i18n.t('dailyInventory.messages.saveError', { defaultValue: 'Something went wrong' }));
  };

  useEffect(() => {
    if (createOrUpdate) {
      void submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrUpdate]);

  return (
    <div className="diForm">
      <Autocomplete
        options={products}
        value={selectedProduct}
        onChange={(_, value) => setProductId(Number(value?.id ?? 0))}
        getOptionLabel={(option) => `${option.code ? `${option.code} - ` : ''}${option.name ?? ''}`}
        isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
        renderInput={(params) => <TextField {...params} label={i18n.t('dailyInventory.fields.product', { defaultValue: 'Product' })} required />}
      />

      <div className="diFormGrid">
        <label className="diField">
          <span>{i18n.t('dailyInventory.fields.date', { defaultValue: 'Date' })}</span>
          <input className="ui-control" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </label>
        <label className="diField">
          <span>{i18n.t('dailyInventory.fields.quantity', { defaultValue: 'Quantity' })}</span>
          <input className="ui-control" type="number" step="0.01" value={quantity} onChange={(event) => setQuantity(event.target.value)} />
        </label>
        <label className="diField">
          <span>{i18n.t('dailyInventory.fields.warningQuantity', { defaultValue: 'Warning quantity' })}</span>
          <input className="ui-control" type="number" step="0.01" value={warningQuantity} onChange={(event) => setWarningQuantity(event.target.value)} />
        </label>
      </div>
    </div>
  );
};

export default DailyInventoryForm;
